import { useEffect, useState } from 'react';

/**
 * Facebook SDK wrapper for the Meta Commerce setup flow. Ported from the
 * nearst dashboard `helpers/metaCommerce.js` so the Facebook OAuth /
 * catalog-creation handshake stays consistent across dashboards.
 *
 * The Facebook JS SDK is loaded on-demand the first time the Meta setup page
 * mounts; once loaded it stays attached to the window so subsequent visits
 * are instant.
 */
export const META_GRAPH_API_VERSION = 'v23.0';

/**
 * Wraps `window.FB.api` in a promise so the catalog / business calls can be
 * awaited like normal async fetches. The SDK callback signature is
 * `(response)` where `response.error` indicates failure.
 */
export const facebookApi = (...fields) =>
	new Promise((resolve, reject) =>
		window.FB.api(...fields, (response) =>
			!response || response.error ? reject(new Error(response?.error?.message || 'Unknown Facebook API error')) : resolve(response)
		)
	);

export const loadFacebookSdk = (callback) => {
	if (document.getElementById('facebook-jssdk')) {
		callback?.();
		return;
	}
	window.fbAsyncInit = function () {
		window.FB.init({
			appId: import.meta.env.VITE_NEARST_APP_ID,
			autoLogAppEvents: true,
			xfbml: true,
			version: META_GRAPH_API_VERSION
		});
		callback?.();
	};
	const js = document.createElement('script');
	const fjs = document.getElementsByTagName('script')[0];
	js.src = 'https://connect.facebook.net/en_US/sdk.js';
	js.id = 'facebook-jssdk';
	fjs.parentNode.insertBefore(js, fjs);
};

/**
 * `null` while the SDK loads; resolves to the `facebookApi` wrapper once
 * `FB.init` finishes so callers can render a spinner in the meantime.
 */
export const useFacebookSdk = () => {
	const [ready, setReady] = useState(false);
	useEffect(() => {
		loadFacebookSdk(() => setReady(true));
	}, []);
	return ready ? facebookApi : null;
};

export const findPages = async (businessId) => {
	const { data: ownedPages } = await facebookApi(`/${businessId}/owned_pages`, 'GET');
	if (ownedPages && ownedPages.length) return ownedPages.map((page) => page.id);
	return [];
};

export const openFBLogin = ({ onAuth, onCancel, onError }) => {
	if (!window.FB) {
		onError?.("Couldn't connect with Facebook. Check your browser privacy settings and try again.");
		return;
	}
	window.FB.login(
		(response) => {
			if (response?.authResponse) {
				onAuth?.(response.authResponse);
			} else if (response?.status === 'unknown') {
				onCancel?.();
			} else {
				onError?.('Could not authenticate with Facebook. Please try again.');
			}
		},
		{
			scope: 'catalog_management,business_management',
			redirect_uri: window.location.href,
			display: 'popup'
		}
	);
};

export const loadBusinesses = async () => {
	const response = await facebookApi('/me/businesses', 'GET', { fields: 'id,name,profile_picture_uri', limit: 1000 });
	return response.data || [];
};

export const findOrCreateCatalog = async (businessId, shop) => {
	const catalogName = `Products from NearSt - ${shop.name}`;
	const { data: existing } = await facebookApi(`/${businessId}/owned_product_catalogs`, 'GET');
	const match = existing.find(({ name }) => name === catalogName);
	if (match) return match.id;
	const created = await facebookApi(`/${businessId}/owned_product_catalogs`, 'POST', {
		name: catalogName,
		vertical: 'commerce'
	});
	return created.id;
};
